import BangaLogoWhite from "@/assets/icons/BFF-Logo-White.svg?react";
import BangaLogoPurple from "@/assets/icons/BFF-Logo-Purple.svg?react";
import React, { useState, useEffect } from "react";
import { LogOut } from "lucide-react";
import { NavLink, useLocation } from "react-router";
import { useTheme } from "@/context/ThemeContext";
import { useAuthStore } from "@/store/useAuthStore";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { NavIcon } from "./NavIcon";
import { useUserPermissions } from "@/hooks/useUserPermissions";



export function Sidebar() {
  const { theme } = useTheme();
  const { user, logout } = useAuthStore();
  const location = useLocation(); // Hook to get current URL
  const queryClient = useQueryClient();
  const { permissions } = useUserPermissions();

  const hasPermission = (perm) => permissions.includes(perm);

  const NAV_GROUPS = [
    ...(hasPermission('project:read:@view-master-project') ||
      hasPermission('project:read:@view-product-development') ||
      hasPermission('project:read:@view-application-lab') ||
      hasPermission('project:read:@view-sensory-lab') ||
      hasPermission('project:read:@view-master-project-schedule') ? [{
        id: "projects-overview",
        label: "Projects Overview",
        iconName: "projects-overview",
        subItems: [
          ...(hasPermission('project:read:@view-master-project') ? [{ id: "master-projects", label: "Master Projects Module", path: "/project-overview/master-projects" }] : []),
          ...(hasPermission('project:read:@view-product-development') ? [{ id: "product-development", label: "Product Development", path: "/project-overview/product-development" }] : []),
          ...(hasPermission('project:read:@view-application-lab') ? [{ id: "application-lab", label: "Application Lab", path: "/project-overview/application-lab" }] : []),
          ...(hasPermission('project:read:@view-sensory-lab') ? [{ id: "sensory", label: "Sensory Testing", path: "/project-overview/sensory" }] : []),
          ...(hasPermission('project:read:@view-master-project-schedule') ? [{ id: "master-project-schedule", label: "Master Project Schedule", path: "/project-overview/master-project-schedule" }] : []),
        ]
      }] : []),
    {
      id: "master-aplication-recipe-list",
      label: "Master Aplication Recipe List",
      iconName: "master-aplication-recipe-list",
      subItems: [
        {
          id: "application-recipes",
          label: "Aplication Recipes",
          path: "/application-recipes",
        },
        {
          id: "bff-product-code-list",
          label: "BFF Product Code List",
          path: "/bff-product-code-list",
        },
        {
          id: "application-categories",
          label: "Aplication Categories",
          path: "/application-categories",
        },
      ],
    },
    {
      id: "team",
      label: "Team Management",
      iconName: "team-management",
      subItems: [
        {
          id: "team-formation",
          label: "Team Formation",
          path: "/team-formation",
        },
      ],
    },
    {
      id: "tasks",
      label: "Task Assignments",
      iconName: "task-management",
      subItems: [
        { id: "project-task", label: "Project Tasks", path: "/project-tasks" },
        { id: "internal-task", label: "Internal Tasks", path: "/internal-tasks" },
      ],
    },
    {
      id: "invites",
      label: "Invites and Access",
      iconName: "invite-access",
      subItems: [
        {
          id: "emp-invites",
          label: "Employee Invitations",
          path: "/employee-invitations",
        },
        {
          id: "emp-mgmt",
          label: "Employee Management",
          path: "/employee-management",
        },
        {
          id: "access-mgmt",
          label: "Access Management",
          path: "/access-management",
        },
        { id: "history", label: "History", path: "/access-history" },
      ],
    },
  ];

  const [activeGroup, setActiveGroup] = useState("invites");
  const [activeSubItem, setActiveSubItem] = useState("emp-invites");
  const [isExpanded, setIsExpanded] = useState(false);
  const timeoutRef = React.useRef(null);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Ignore errors, maybe token already expired
    }
    logout();
    queryClient.clear();
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, 800); // 800ms delay before collapsing
  };

  // --- NEW: Sync State with URL ---
  useEffect(() => {
    const currentPath = location.pathname;

    // Loop through groups to find the matching route
    for (const group of NAV_GROUPS) {
      // 1. Check sub-items
      const matchingSubItem = group.subItems.find(
        (subItem) =>
          // Use startsWith so nested routes (e.g., /employee-management/123) keep the parent active
          currentPath === subItem.path ||
          currentPath.startsWith(`${subItem.path}/`)
      );

      if (matchingSubItem) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setActiveGroup(group.id);
        setActiveSubItem(matchingSubItem.id);
        return; // Match found, exit loop
      }
    }
  }, [location.pathname]); // Re-run whenever URL changes

  return (
    <nav
      className={`hidden md:flex flex-col h-full border-r shadow-lg bg-background border-table-stroke overflow-x-hidden transition-all duration-300 ease-in-out z-50 ${isExpanded ? "w-80" : "w-20"
        }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo */}
      <div
        className={`transition-all duration-300 border-b border-table-stroke flex items-center justify-center p-4 ${isExpanded ? "h-28" : "h-16"
          }`}
      >
        {theme === "dark" ? (
          <BangaLogoWhite className="w-full h-full" />
        ) : (
          <BangaLogoPurple className="w-full h-full" />
        )}
      </div>

      {/* Navigation Links */}
      <nav className="relative flex flex-col flex-1 gap-2 pt-8">
        {/* Sliding pill background indicator */}
        <div
          className="absolute left-0 right-0 z-0 transition-all duration-500 ease-in-out pointer-events-none"
          style={{
            transform: `translateY(${NAV_GROUPS.findIndex((g) => g.id === activeGroup) * 66
              }px)`,
            height: "58px",
          }}
        >
          <div className="absolute inset-y-0 right-0 rounded-l-full left-4 bg-[#eeebf4] dark:bg-primary-shade-2">
            <div
              className={`absolute -top-5 right-0 w-5 h-5 bg-transparent rounded-br-full z-10 pointer-events-none ${theme === "dark"
                ? "shadow-[5px_5px_0_5px_#2c213d]"
                : "shadow-[5px_5px_0_5px_#eeebf4]"
                }`}
            />
            <div
              className={`absolute -bottom-5 right-0 w-5 h-5 bg-transparent rounded-tr-full z-10 pointer-events-none ${theme === "dark"
                ? "shadow-[5px_-5px_0_5px_#2c213d]"
                : "shadow-[5px_-5px_0_5px_#eeebf4]"
                }`}
            />
          </div>
        </div>

        {/* Group items */}
        {NAV_GROUPS.map((group) => {
          const isActive = activeGroup === group.id;
          // Only expand submenu if group is active AND sidebar is expanded
          const showSubItems =
            isActive && group.subItems.length > 0 && isExpanded;

          return (
            <div key={group.id} className="relative">
              <button
                onClick={() => setActiveGroup(group.id)}
                className={`w-full flex items-center gap-4 mx-8 py-4 transition-all duration-200 relative z-10 cursor-pointer ${isActive
                  ? "text-nav-highlight font-semibold"
                  : "text-gray-900 dark:text-foreground"
                  }`}
              >
                <NavIcon
                  name={group.iconName}
                  className={`w-6 h-6 transition-colors ${isActive
                    ? "text-nav-highlight"
                    : "text-gray-600 dark:text-foreground"
                    } hover:text-nav-highlight`}
                />
                <span
                  className={`text-base font-medium tracking-tight whitespace-nowrap transition-opacity duration-200 ${isExpanded
                    ? "opacity-100 delay-100"
                    : "opacity-0 w-0 overflow-hidden"
                    }`}
                >
                  {group.label}
                </span>
              </button>

              {/* Sub menu */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${showSubItems ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <div
                  className={`mt-1 mb-4 ml-0 space-y-1 transform transition-all duration-300 ease-in-out ${showSubItems ? "translate-y-0" : "-translate-y-2"
                    }`}
                >
                  {group.subItems.map((subItem) => {
                    const isSubActive = activeSubItem === subItem.id;
                    return (
                      <NavLink
                        to={subItem.path}
                        key={subItem.id}
                        onClick={() => setActiveSubItem(subItem.id)}
                        className={`w-full block text-left pl-20 pr-4 py-2 text-sm cursor-pointer transition-colors whitespace-nowrap ${isSubActive
                          ? "text-nav-highlight font-medium"
                          : "text-gray-600 dark:text-foreground hover:text-foreground font-normal dark:hover:text-foreground"
                          }`}
                      >
                        {subItem.label}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* User Profile at bottom */}
      <div className="p-4 border-t border-table-stroke">
        <div
          className={`flex items-center ${isExpanded ? "justify-between" : "justify-center"
            } gap-3 transition-all duration-300`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 overflow-hidden rounded-full min-w-10 bg-primary-shade-2">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt="avatar"
                  className="object-cover w-full h-full"
                />
              ) : null}
            </div>
            <div
              className={`transition-opacity duration-200 ${isExpanded ? "opacity-100 block" : "opacity-0 hidden"
                }`}
            >
              <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                {user?.name || "Guest"}
              </p>
              <p className="text-xs text-gray-500 whitespace-nowrap">
                {user?.email || ""}
              </p>
            </div>
          </div>
          {isExpanded && (
            <button
              onClick={handleLogout}
              className="w-10 h-10 text-center rounded cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <LogOut className="mx-auto" size={25} />
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
